/**
 * `hour` — `HourValue` (`specs/M2-tier2-column-types.md` §5.11, `[09§A.7 hour]`).
 *
 * A wall-clock time with **no date and no timezone**: `{"hour":10,"minute":30,"changed_at":…}`,
 * stored 24-hour. `minute` is often absent and is treated as 0 `[cvs§2 hour]`.
 *
 * **`text` is 12-hour, zero-padded, with a space before the meridiem** — measured over the 42
 * populated cells in the 2026-09-10 snapshot: `{"hour":2,"minute":48}` → `"02:48 AM"`,
 * `{"hour":12}` → `"12:00 PM"`, `{"hour":13}` → `"01:00 PM"`, `{"hour":23,"minute":34}` →
 * `"11:34 PM"`. `hour: 0` never occurs on this account; `"12:00 AM"` is the conservative
 * reading of the same rule (ASSUMED, q333).
 *
 * `settings.format` (`"24H"` | `"12H"`, default `"12H"`) is **display only** — it never changes
 * the stored value, and monday's own `ColumnValue.text` stayed 12-hour on every sampled column.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { pad2, textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const hourValueSchema = z.looseObject({
    hour: z.number().int().min(0).max(23),
    minute: z.number().int().min(0).max(59).optional(),
    changed_at: z.string().nullable().optional(),
});
import { hourFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const hourSettingsSchema = z.looseObject({
    /** `"24H"` | `"12H"`; *"If omitted, defaults to `12H`"* `[09§A.7 hour]`. */
    format: z.string().optional(),
    hide_footer: z.boolean().optional(),
});
function coerceInt(raw, field) {
    const parsed = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (!Number.isInteger(parsed))
        throw new ColumnValueError('hour', `"${field}" must be a whole number`);
    return parsed;
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed === '')
            return null;
        const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
        if (match === null)
            throw new ColumnValueError('hour', `"${raw}" is not an HH:mm time`);
        raw = { hour: Number(match[1]), minute: Number(match[2]) };
    }
    if (typeof raw !== 'object')
        throw new ColumnValueError('hour', `expected an object, received ${typeof raw}`);
    const record = raw;
    if (record.hour === null || record.hour === undefined || record.hour === '')
        return null;
    const hour = coerceInt(record.hour, 'hour');
    const minute = record.minute === null || record.minute === undefined ? 0 : coerceInt(record.minute, 'minute');
    if (hour < 0 || hour > 23)
        throw new ColumnValueError('hour', `hour ${hour} is outside 0..23`);
    if (minute < 0 || minute > 59)
        throw new ColumnValueError('hour', `minute ${minute} is outside 0..59`);
    const value = { hour, minute };
    // monday ships `"changed_at": null` on many cells; keeping the key (null included) is
    // what makes a re-export byte-identical `[cvs§4.2]`.
    if (keepVolatile && 'changed_at' in record)
        value.changed_at = record.changed_at;
    return value;
}
/** monday's `text`: `"02:48 AM"`, `"12:00 PM"`, `"11:34 PM"` (measured). */
export function hourText(value) {
    const hour24 = value.hour;
    const meridiem = hour24 < 12 ? 'AM' : 'PM';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${pad2(hour12)}:${pad2(value.minute ?? 0)} ${meridiem}`;
}
/** Minutes since midnight — the sort/filter shadow, so 23:00 sorts after 09:00. */
export function hourMinutes(value) {
    return value.hour * 60 + (value.minute ?? 0);
}
export const hourColumn = defineColumnType({
    type: 'hour',
    title: 'Hour',
    description: 'A time of day, with no date attached.',
    apiFragmentName: 'HourValue',
    legacyTypeName: 'hour',
    idPrefix: 'hour',
    valueSchema: hourValueSchema,
    settingsSchema: hourSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: hourSettingsSchema,
    writable: true,
    /** *"The `change_simple_column_value` mutation is not supported"* `[09§A.7 hour]`. */
    supportsSimpleValue: false,
    clearForms: ['null'],
    filterConditions: hourFilterConditions,
    filterOperators: operatorsOf(hourFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    toText(value) {
        return value === null ? '' : hourText(value);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        return { text: textShadow(hourText(value)), number: hourMinutes(value), date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.hour === b.hour && (a.minute ?? 0) === (b.minute ?? 0);
    },
    toActivityValue(value) {
        return value === null ? null : { hour: value.hour, minute: value.minute ?? 0 };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { hour: value.hour, minute: value.minute ?? 0 };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue(value) {
        return value === null ? null : `${pad2(value.hour)}:${pad2(value.minute ?? 0)}`;
    },
    fromMondaySettings(raw) {
        return hourSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return { hour: value.hour, minute: value.minute ?? 0, updated_at: toFragmentTimestamp(value.changed_at) };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /**
     * monday's own filter offers **named ranges** (Early morning 4–7 AM, Morning 7 AM–12 PM,
     * Afternoon 12–8 PM, Evening 8–11 PM, Night 11 PM–4 AM) `[09§A.7 hour]`, so the buckets are
     * those ranges rather than one bucket per minute.
     */
    bucketsOf(value) {
        if (value === null)
            return [];
        const hour = value.hour;
        const bucket = hour >= 4 && hour < 7
            ? 'early_morning'
            : hour >= 7 && hour < 12
                ? 'morning'
                : hour >= 12 && hour < 20
                    ? 'afternoon'
                    : hour >= 20 && hour < 23
                        ? 'evening'
                        : 'night';
        const labels = {
            early_morning: 'Early morning',
            morning: 'Morning',
            afternoon: 'Afternoon',
            evening: 'Evening',
            night: 'Night',
        };
        return [{ key: bucket, label: labels[bucket] ?? bucket }];
    },
    filterSql(ctx) {
        return defaultFilterSql('number', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map